import {
  INTEREST_BOXES,
  DIVIDEND_BOXES,
  INVESTMENT_PROFILE,
  INVESTMENT_CONFIRMATIONS,
} from "./investment-calculator.ts";
export type Statement = Record<string, string>;
export type InvestmentEntries = {
  profile?: string;
  interestStatements?: Statement[];
  dividendStatements?: Statement[];
  investmentConfirmations?: Record<string, boolean>;
};
export const statementBoxes = (
  kind: "interestStatements" | "dividendStatements",
) => (kind === "interestStatements" ? INTEREST_BOXES : DIVIDEND_BOXES);
export const commonStatementBoxes = (
  kind: "interestStatements" | "dividendStatements",
) =>
  kind === "interestStatements"
    ? ["box1", "box3", "box4", "box8"]
    : ["box1a", "box1b", "eligibleQualified", "box2a", "box4", "box12"];
// Blank is never silently converted to zero. The explicit "remaining boxes are
// blank" control is the only bulk-zero action and is visible to the preparer.
export const newStatement = (
  kind: "interestStatements" | "dividendStatements",
): Statement =>
  Object.fromEntries([
    ["payer", ""],
    ...statementBoxes(kind).map(([k]) => [k, ""]),
  ]);
export const normalizeDollars = (s: string) =>
  /^(0|[1-9]\d*)(\.\d{1,2})?$/.test(s.trim())
    ? s.trim().split(".")[0] +
      "." +
      (s.trim().split(".")[1] || "").padEnd(2, "0")
    : s;
export function editInvestmentEntries<
  T extends InvestmentEntries & { confirmations: Record<string, boolean> },
>(
  input: T,
  kind: "interestStatements" | "dividendStatements",
  rows: Statement[],
): T {
  const next: InvestmentEntries & { confirmations: Record<string, boolean> } = {
    ...input,
    profile: INVESTMENT_PROFILE,
    interestStatements: input.interestStatements || [],
    dividendStatements: input.dividendStatements || [],
    [kind]: rows,
    investmentConfirmations: {},
    confirmations: { ...input.confirmations, income: false, payments: false },
  };
  if (
    (next.interestStatements?.length || 0) +
      (next.dividendStatements?.length || 0) ===
    0
  ) {
    delete next.profile;
    delete next.interestStatements;
    delete next.dividendStatements;
    delete next.investmentConfirmations;
  }
  return next as T;
}
export function investmentEntryProblems(input: InvestmentEntries) {
  const problems: { id: string; message: string }[] = [];
  if (input.profile !== INVESTMENT_PROFILE) return problems;
  for (const kind of ["interestStatements", "dividendStatements"] as const)
    (input[kind] || []).forEach((row, i) => {
      const section = `${kind === "interestStatements" ? "Interest (1099-INT)" : "Dividends (1099-DIV)"} ${i + 1}`;
      if (!row.payer.trim())
        problems.push({
          id: `calc-${kind}-${i}-payer`,
          message: `${section} — Payer: enter the statement payer or a distinct label.`,
        });
      for (const [id, label] of statementBoxes(kind))
        if (!/^(0|[1-9]\d{0,8})\.\d{2}$/.test(normalizeDollars(row[id])))
          problems.push({
            id: `calc-${kind}-${i}-${id}`,
            message: `${section} — ${label}: enter dollars and cents, or explicitly confirm a blank box as zero.`,
          });
    });
  for (const item of INVESTMENT_CONFIRMATIONS)
    if (input.investmentConfirmations?.[item.id] !== true)
      problems.push({
        id: "calc-investment-" + item.id,
        message: `Statement review — ${item.label}: ${item.detail}`,
      });
  return problems;
}
export function normalizedInvestments(
  input: InvestmentEntries,
): InvestmentEntries {
  if (input.profile !== INVESTMENT_PROFILE) return {};
  const out: InvestmentEntries = {
    profile: input.profile,
    investmentConfirmations: input.investmentConfirmations,
  };
  for (const kind of ["interestStatements", "dividendStatements"] as const)
    out[kind] = (input[kind] || []).map((row) => ({
      ...row,
      ...Object.fromEntries(
        statementBoxes(kind).map(([k]) => [k, normalizeDollars(row[k])]),
      ),
    }));
  return out;
}
